<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'
import type { RouteLocationPathRaw } from 'vue-router'
import FeedActionDrawer from '@/components/feed/FeedActionDrawer.vue'
import ListingScreen from '@/components/listing/ListingScreen.vue'
import { Button } from '@/components/ui/button'
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { Spinner } from '@/components/ui/spinner'
import { useListingActions } from '@/composables/listing/use-listing-actions'
import { listingPagePath, loadListing, toListingCardModel, useListingPreview, type ListingPreviewData } from '@/composables/listing/use-listing-preview'
import ResponseActions from '@/components/responses/ResponseActions.vue'
import { responseListingLocation } from '@/composables/responses/response-navigation'

// One instance lives in the default layout and follows `?summary=` /
// `?vacancy=` in the URL (see useListingPreview). It shows the same two-item
// screen as the listing page over the list it was opened from, so the list
// keeps its scroll, loaded pages and a chat its draft.

const route = useRoute()
const router = useRouter()
const isDesktop = useMediaQuery('(min-width: 768px)')
const { target, close, previewLocation } = useListingPreview()

const mounted = ref(false)
const preview = ref<ListingPreviewData | null>(null)
const loading = ref(false)
const errorMessage = ref('')
const screen = useTemplateRef<{ showDetails: () => void }>('screen')
let requestId = 0

const open = computed(() => mounted.value && target.value !== null)
const responseId = computed(() => target.value?.responseId)
const listing = useListingActions(preview, responseId, () => screen.value?.showDetails())
const { actions } = listing
const title = computed(() => preview.value
  ? toListingCardModel(preview.value.item).title
  : target.value?.kind === 'summary' ? 'Резюме' : 'Вакансия')
const fullPagePath = computed(() => {
  if (!target.value) return '/'
  if (!responseId.value) return listingPagePath(target.value.kind, target.value.id)
  const returnTo = router.resolve({ path: route.path, query: { ...route.query, summary: undefined, vacancy: undefined, previewResponse: undefined } }).fullPath
  return router.resolve(responseListingLocation(target.value.kind, target.value.id, responseId.value, returnTo)).href
})

// Another vacancy of the company replaces this preview: closing returns to the list.
function linkTo(id: string) {
  return { ...(previewLocation('vacancy', id) as RouteLocationPathRaw), replace: true }
}

async function load(kind: 'summary' | 'vacancy', id: string) {
  const current = ++requestId
  loading.value = true
  preview.value = null
  errorMessage.value = ''
  const result = await loadListing(kind, id)
  if (current !== requestId) return
  if (result.ok) preview.value = result.data
  else errorMessage.value = result.message
  loading.value = false
}

watch(target, (value) => {
  if (!mounted.value) return
  actions.closeActionSheet()
  if (value !== null) void load(value.kind, value.id)
})

onMounted(() => {
  mounted.value = true
  if (target.value !== null) void load(target.value.kind, target.value.id)
})

function onOpenChange(value: boolean) {
  if (!value && target.value !== null) void close()
}
</script>

<template>
  <Drawer
    v-if="mounted"
    :open="open"
    :direction="isDesktop ? 'right' : 'bottom'"
    :should-scale-background="false"
    @update:open="onOpenChange"
  >
    <DrawerContent
      data-testid="listing-preview"
      :class="[
        'gap-0 overflow-hidden border-0 bg-black p-0 text-white [&>div:first-child]:hidden',
        isDesktop ? '!w-[min(26rem,100vw)] !max-w-none rounded-l-2xl' : '!h-[calc(100dvh-3rem)] !max-h-none rounded-t-2xl',
      ]"
    >
      <DrawerHeader class="sr-only">
        <DrawerTitle>{{ title }}</DrawerTitle>
        <DrawerDescription>Просмотр {{ target?.kind === 'vacancy' ? 'вакансии' : 'резюме' }}</DrawerDescription>
      </DrawerHeader>

      <div class="relative min-h-0 flex-1">
        <span v-if="!isDesktop" class="pointer-events-none absolute left-1/2 top-2 z-50 h-1 w-10 -translate-x-1/2 rounded-full bg-white/70" aria-hidden="true" />

        <div v-if="loading && !preview" class="flex size-full items-center justify-center" aria-live="polite">
          <Spinner class="size-8 text-white" />
          <span class="sr-only">Загружаем публикацию</span>
        </div>

        <div v-else-if="errorMessage" class="flex size-full flex-col items-center gap-4 overflow-y-auto px-8 py-10 text-center" role="alert">
          <Icon name="tabler:alert-triangle-filled" class="size-10 text-white/80" aria-hidden="true" />
          <p class="text-base">{{ errorMessage }}</p>
          <div v-if="responseId && target" class="w-full rounded-2xl bg-card p-4 text-left text-foreground">
            <p class="mb-3 text-sm">Публикация недоступна</p>
            <ResponseActions :key="responseId" :response-id="responseId" :listing-id="target.id" :kind="target.kind" @changed="listing.onResponseChanged" />
          </div>
          <div class="flex gap-2">
            <Button variant="secondary" shape="pill" @click="target && load(target.kind, target.id)">Повторить</Button>
            <Button variant="ghost" shape="pill" class="text-white hover:bg-white/10 hover:text-white" @click="close()">Закрыть</Button>
          </div>
        </div>

        <ListingScreen
          v-else-if="preview"
          ref="screen"
          :key="preview.item.id"
          :data="preview"
          :response-id="responseId"
          :favorite-active="listing.favoriteActive.value"
          :has-response="listing.hasResponse.value"
          :can-act="listing.canAct.value"
          :primary-label="listing.primaryLabel.value"
          :is-owner="listing.isOwner.value"
          :visible="open"
          :link-to="linkTo"
          sheet
          closable
          @action="listing.onAction"
          @close="close()"
        >
          <template v-if="responseId && target" #context>
            <div class="border-b border-border p-4">
              <ResponseActions :key="responseId" :response-id="responseId" :listing-id="target.id" :kind="target.kind" @changed="listing.onResponseChanged" />
            </div>
          </template>
        </ListingScreen>
        <a v-if="preview" :href="fullPagePath" class="sr-only focus:not-sr-only focus:absolute focus:bottom-2 focus:left-2 focus:z-50 focus:rounded focus:bg-card focus:p-3 focus:text-primary">Открыть отдельной страницей</a>
      </div>
    </DrawerContent>

    <FeedActionDrawer
      :mode="actions.actionSheet.value"
      :summaries="actions.publishedSummaries.value"
      nested
      @update:mode="actions.actionSheet.value = $event"
      @responded="listing.onResponded"
    />
  </Drawer>
</template>
